import axios from "axios";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createRequestWithAuthHeaders,
  getFullUrl,
} from "../configs/axios-export.custom";
import { UserContextType } from "../services/UserContext";
import { RequestStatus } from "../utils/Helpers/fetchStatus";
import { buildQuery } from "../utils/Helpers/queryBuilder";
import { ColumnsSettingsData } from "../components/MaterialTable/Table";
import { ContractsFiltersType } from "./contracts";
import { GrantsFiltersType } from "./grants";

export const CONTRACTS_SAVED_FILTERS_KEY = "contractsSavedFiltersKey";
export const GRANTS_SAVED_FILTERS_KEY = "grantsSavedFiltersKey";

export interface SavedFilterPreset<TFilters> {
  id: string;
  name: string;
  filters: TFilters;
  createdUtc: string;
  updatedUtc: string;
}

export interface SavedFiltersPayload<TFilters> {
  version: 1;
  defaultId: string | null;
  presets: Array<SavedFilterPreset<TFilters>>;
}

export type SavedFiltersPage = "contracts" | "grants";

const emptyPayload = <TFilters>(): SavedFiltersPayload<TFilters> => ({
  version: 1,
  defaultId: null,
  presets: [],
});

interface UserSettingsState {
  tableSettings: {
    columnsOrder: Array<ColumnsSettingsData>;
    fetchStatus: string;
    patchFetchStatus: string;
    deleteFetchStatus: string;
  };
  userSettings: {
    setting: Record<string, string | number>;
    fetchStatus: string;
    patchFetchStatus: string;
    deleteFetchStatus: string;
  };
  savedFilters: {
    contracts: {
      payload: SavedFiltersPayload<ContractsFiltersType>;
      fetchStatus: string;
      patchFetchStatus: string;
    };
    grants: {
      payload: SavedFiltersPayload<GrantsFiltersType>;
      fetchStatus: string;
      patchFetchStatus: string;
    };
  };
}

const initialState: UserSettingsState = Object.freeze({
  tableSettings: {
    columnsOrder: [],
    fetchStatus: RequestStatus.statuses.NULL,
    patchFetchStatus: RequestStatus.statuses.NULL,
    deleteFetchStatus: RequestStatus.statuses.NULL,
  },
  userSettings: {
    setting: {},
    fetchStatus: RequestStatus.statuses.NULL,
    patchFetchStatus: RequestStatus.statuses.NULL,
    deleteFetchStatus: RequestStatus.statuses.NULL,
  },
  savedFilters: {
    contracts: {
      payload: emptyPayload<ContractsFiltersType>(),
      fetchStatus: RequestStatus.statuses.NULL,
      patchFetchStatus: RequestStatus.statuses.NULL,
    },
    grants: {
      payload: emptyPayload<GrantsFiltersType>(),
      fetchStatus: RequestStatus.statuses.NULL,
      patchFetchStatus: RequestStatus.statuses.NULL,
    },
  },
});

const savedFiltersKeyFor = (page: SavedFiltersPage): string =>
  page === "contracts"
    ? CONTRACTS_SAVED_FILTERS_KEY
    : GRANTS_SAVED_FILTERS_KEY;

export const getTableSettings: any = createAsyncThunk(
  "userSettings/getTableSettings",
  async (data: { context: UserContextType; key: string }, thunkAPI) => {
    const { context, key } = data;
    const response = await axios.get(
      getFullUrl(`/api/UserSetting${buildQuery({ key })}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    );

    return { data: response?.data ?? [], key };
  },
);

export const patchTableSettings: any = createAsyncThunk(
  "userSettings/patchTableSettings",
  async (
    data: {
      context: UserContextType;
      key: string;
      configuration: { [key: string]: Array<ColumnsSettingsData> };
    },
    thunkAPI,
  ) => {
    const { context, key, configuration } = data;
    const response = await axios.patch(
      getFullUrl(`/api/UserSetting/${key}`, {
        useDedicatedEnvironment: true,
      }),
      configuration,
      createRequestWithAuthHeaders(context),
    );

    // Return both the response and the configuration data
    // so the fulfilled reducer can update the state immediately
    return { 
      data: response?.data ?? [], 
      key,
      configuration 
    };
  },
);

export const deleteTableSettings: any = createAsyncThunk(
  "userSettings/deleteTableSettings",
  async (data: { context: UserContextType; key: string }, thunkAPI) => {
    const { context, key } = data;
    return await axios.delete(
      getFullUrl(`/api/UserSetting/${key}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    );
  },
);

export const getUserSetting: any = createAsyncThunk(
  "userSettings/getUserSetting",
  async (data: { context: UserContextType; key: string }, thunkAPI) => {
    const { context, key } = data;
    const response = await axios.get(
      getFullUrl(`/api/UserSetting${buildQuery({ key })}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    );

    return { data: response?.data ?? [], key };
  },
);

export const patchUserSettings: any = createAsyncThunk(
  "userSettings/patchUserSettings",
  async (
    data: {
      context: UserContextType;
      key: string;
      setting: Record<string, string | number>;
    },
    thunkAPI,
  ) => {
    const { context, key, setting } = data;
    const response = await axios.patch(
      getFullUrl(`/api/UserSetting/${key}`, {
        useDedicatedEnvironment: true,
      }),
      setting,
      createRequestWithAuthHeaders(context),
    );

    return response?.data ?? [];
  },
);

export const deleteUserSettings: any = createAsyncThunk(
  "userSettings/deleteUserSettings",
  async (data: { context: UserContextType; key: string }, thunkAPI) => {
    const { context, key } = data;
    return await axios.delete(
      getFullUrl(`/api/UserSetting/${key}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    );
  },
);

export const getSavedFilters: any = createAsyncThunk(
  "userSettings/getSavedFilters",
  async (
    data: { context: UserContextType; page: SavedFiltersPage },
    thunkAPI,
  ) => {
    const { context, page } = data;
    const key = savedFiltersKeyFor(page);
    const response = await axios.get(
      getFullUrl(`/api/UserSetting${buildQuery({ key })}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    );
    return { data: response?.data ?? null, key, page };
  },
);

export const patchSavedFilters: any = createAsyncThunk(
  "userSettings/patchSavedFilters",
  async (
    data: {
      context: UserContextType;
      page: SavedFiltersPage;
      payload: SavedFiltersPayload<any>;
    },
    thunkAPI,
  ) => {
    const { context, page, payload } = data;
    const key = savedFiltersKeyFor(page);
    const response = await axios.patch(
      getFullUrl(`/api/UserSetting/${key}`, {
        useDedicatedEnvironment: true,
      }),
      { [key]: payload },
      createRequestWithAuthHeaders(context),
    );
    return { data: response?.data ?? null, key, page, payload };
  },
);

const slice = createSlice({
  name: "userSettings",
  initialState,
  reducers: {
    resetTableSettings(state: UserSettingsState) {
      state.tableSettings = initialState.tableSettings;
    },
  },
  extraReducers: {
    [getTableSettings.pending]: (state, action) => {
      state.tableSettings.fetchStatus = RequestStatus.statuses.FETCHING;
    },
    [getTableSettings.fulfilled]: (state, action: PayloadAction<any>) => {
      state.tableSettings.columnsOrder =
        action.payload.data.setting[action.payload.data.key];
      state.tableSettings.fetchStatus = RequestStatus.statuses.DONE;
    },
    [getTableSettings.rejected]: (state, action) => {
      state.tableSettings.fetchStatus = RequestStatus.statuses.ERROR;
    },

    [patchTableSettings.pending]: (state, action) => {
      state.tableSettings.patchFetchStatus = RequestStatus.statuses.FETCHING;
    },
    [patchTableSettings.fulfilled]: (state, action: PayloadAction<any>) => {
      // Update the columnsOrder state immediately with the saved configuration
      // This ensures Redux state is in sync without needing to fetch again
      const { key, configuration } = action.payload;
      if (configuration && configuration[key]) {
        state.tableSettings.columnsOrder = configuration[key];
      }
      state.tableSettings.patchFetchStatus = RequestStatus.statuses.DONE;
    },
    [patchTableSettings.rejected]: (state, action) => {
      state.tableSettings.patchFetchStatus = RequestStatus.statuses.ERROR;
    },

    [deleteTableSettings.pending]: (state, action) => {
      state.tableSettings.deleteFetchStatus = RequestStatus.statuses.FETCHING;
    },
    [deleteTableSettings.fulfilled]: (state, action: PayloadAction<any>) => {
      state.tableSettings.deleteFetchStatus = RequestStatus.statuses.DONE;
    },
    [deleteTableSettings.rejected]: (state, action) => {
      state.tableSettings.deleteFetchStatus = RequestStatus.statuses.ERROR;
    },

    [getUserSetting.pending]: (state, action) => {
      state.userSettings.fetchStatus = RequestStatus.statuses.FETCHING;
    },
    [getUserSetting.fulfilled]: (state, action: PayloadAction<any>) => {
      state.userSettings.setting =
        action.payload.data.setting.animationShownFor;
      state.userSettings.fetchStatus = RequestStatus.statuses.DONE;
    },
    [getUserSetting.rejected]: (state, action) => {
      state.userSettings.fetchStatus = RequestStatus.statuses.ERROR;
    },

    [patchUserSettings.pending]: (state, action) => {
      state.userSettings.patchFetchStatus = RequestStatus.statuses.FETCHING;
    },
    [patchUserSettings.fulfilled]: (state, action: PayloadAction<any>) => {
      state.userSettings.patchFetchStatus = RequestStatus.statuses.DONE;
    },
    [patchUserSettings.rejected]: (state, action) => {
      state.userSettings.patchFetchStatus = RequestStatus.statuses.ERROR;
    },

    [deleteUserSettings.pending]: (state, action) => {
      state.userSettings.deleteFetchStatus = RequestStatus.statuses.FETCHING;
    },
    [deleteUserSettings.fulfilled]: (state, action: PayloadAction<any>) => {
      state.userSettings.deleteFetchStatus = RequestStatus.statuses.DONE;
    },
    [deleteUserSettings.rejected]: (state, action) => {
      state.userSettings.deleteFetchStatus = RequestStatus.statuses.ERROR;
    },

    [getSavedFilters.pending]: (state, action) => {
      const { page } = action.meta.arg;
      state.savedFilters[page].fetchStatus = RequestStatus.statuses.FETCHING;
    },
    [getSavedFilters.fulfilled]: (state, action: PayloadAction<any>) => {
      const { page, key, data } = action.payload;
      const fromServer = data?.setting?.[key];
      const normalized =
        fromServer &&
        typeof fromServer === "object" &&
        Array.isArray(fromServer.presets)
          ? {
              version: 1 as const,
              defaultId:
                fromServer.defaultId &&
                fromServer.presets.some(
                  (p: any) => p.id === fromServer.defaultId,
                )
                  ? fromServer.defaultId
                  : null,
              presets: fromServer.presets,
            }
          : { version: 1 as const, defaultId: null, presets: [] };
      state.savedFilters[page].payload = normalized;
      state.savedFilters[page].fetchStatus = RequestStatus.statuses.DONE;
    },
    [getSavedFilters.rejected]: (state, action) => {
      const { page } = action.meta.arg;
      state.savedFilters[page].payload = {
        version: 1,
        defaultId: null,
        presets: [],
      };
      state.savedFilters[page].fetchStatus = RequestStatus.statuses.ERROR;
    },

    [patchSavedFilters.pending]: (state, action) => {
      const { page } = action.meta.arg;
      state.savedFilters[page].patchFetchStatus =
        RequestStatus.statuses.FETCHING;
    },
    [patchSavedFilters.fulfilled]: (state, action: PayloadAction<any>) => {
      const { page, payload } = action.payload;
      state.savedFilters[page].payload = payload;
      state.savedFilters[page].patchFetchStatus = RequestStatus.statuses.DONE;
    },
    [patchSavedFilters.rejected]: (state, action) => {
      const { page } = action.meta.arg;
      state.savedFilters[page].patchFetchStatus = RequestStatus.statuses.ERROR;
    },
  },
});

export const { reducer } = slice;

export const { resetTableSettings } = slice.actions;
export default slice;
